/**
 * ArchivedFitnessMembersPage — list archived fitness members with restore + hard-delete.
 * Hard-delete requires typing 'DELETE' and calls the external 606 Fitness DELETE.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, RotateCcw, Trash2, Search, Users, AlertTriangle, Mail, Phone,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function memberName(r) {
  return r.snapshot?.name || r.snapshot?.full_name || r.snapshot?.email || r.user_id;
}

export default function ArchivedFitnessMembersPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/fitness-archive/users`, { headers: getAuthHeaders() });
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحميل الأرشيف' : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const restore = async (rec) => {
    setProcessingId(rec.user_id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/fitness-archive/users/${rec.user_id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم استعادة العضو من الأرشيف' : 'Member restored from archive');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setProcessingId(null);
    }
  };

  const hardDelete = async () => {
    if (!confirmDelete || confirmInput.trim().toUpperCase() !== 'DELETE') return;
    setProcessingId(confirmDelete.user_id);
    try {
      const res = await axios.delete(
        `${BACKEND_URL}/api/admin/fitness-archive/users/${confirmDelete.user_id}/hard`,
        { headers: getAuthHeaders() },
      );
      if (res.data?.external_delete_error) {
        toast.warning(
          (language === 'ar' ? 'حُذف من الأرشيف لكن تعذّر حذفه من خدمة 606: ' : 'Removed from archive but external delete failed: ')
          + res.data.external_delete_error,
        );
      } else {
        toast.success(language === 'ar' ? 'تم الحذف النهائي' : 'Permanently deleted');
      }
      setConfirmDelete(null);
      setConfirmInput('');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setProcessingId(null);
    }
  };

  const visible = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const s = r.snapshot || {};
    return [memberName(r), s.email, s.phone, r.user_id]
      .some((v) => v && String(v).toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title="أرشيف أعضاء 606 Fitness"
      titleEn="Archived 606 Fitness Members"
      subtitle={language === 'ar' ? `${items.length} عضو مؤرشف` : `${items.length} archived members`}
      subtitleEn={`${items.length} archived members`}
      screenCode="ARC-005"
      backPath="/control/archive"
      onBack={() => navigate('/control/archive')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: '606 FITNESS', labelEn: '606 FITNESS' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="archived-fitness-page">
        <div className="relative max-w-md mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'بحث بالاسم/البريد/الهاتف...' : 'Search by name/email/phone...'}
            className="w-full ps-3 pe-9 py-2.5 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-emerald-500/50 focus:outline-none"
            data-testid="archived-fit-search"
          />
        </div>

        <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#808080]">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا يوجد أعضاء مؤرشفون' : 'No archived members'}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#2a2a4e]/50">
              {visible.map((rec) => {
                const s = rec.snapshot || {};
                return (
                  <div
                    key={rec.user_id}
                    className="p-4 flex items-center justify-between gap-3 hover:bg-[#0a0a0a]/40"
                    data-testid={`archived-fit-row-${rec.user_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium">{memberName(rec)}</span>
                        {s.role && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
                            {s.role}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#808080] mt-1 flex items-center gap-3 flex-wrap">
                        {s.email && (
                          <span className="flex items-center gap-1" dir="ltr">
                            <Mail className="h-3 w-3" /> {s.email}
                          </span>
                        )}
                        {s.phone && (
                          <span className="flex items-center gap-1" dir="ltr">
                            <Phone className="h-3 w-3" /> {s.phone}
                          </span>
                        )}
                        {rec.archived_at && (
                          <span className="text-amber-400">
                            {language === 'ar' ? 'أُرشف:' : 'archived:'} {new Date(rec.archived_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => restore(rec)}
                        disabled={processingId === rec.user_id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                        data-testid={`restore-fit-${rec.user_id}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5 me-1" />
                        {language === 'ar' ? 'استعادة' : 'Restore'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => { setConfirmDelete(rec); setConfirmInput(''); }}
                        disabled={processingId === rec.user_id}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8"
                        data-testid={`harddel-fit-${rec.user_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 me-1" />
                        {language === 'ar' ? 'حذف نهائي' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setConfirmInput(''); } }}
      >
        <DialogContent className="bg-[#1a1a2e] border-red-500/30 text-white" data-testid="harddel-fit-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {language === 'ar' ? 'تأكيد الحذف النهائي' : 'Confirm permanent delete'}
            </DialogTitle>
            <DialogDescription className="text-[#a0a0a0]">
              {language === 'ar'
                ? <>سيتم حذف <span className="text-white">{confirmDelete && memberName(confirmDelete)}</span> نهائياً من خدمة 606 Fitness. اكتب <code className="text-red-400 font-mono mx-1">DELETE</code> للتأكيد.</>
                : <>This will permanently delete <span className="text-white">{confirmDelete && memberName(confirmDelete)}</span> from the 606 Fitness service. Type <code className="text-red-400 font-mono mx-1">DELETE</code> to confirm.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="DELETE"
              className="bg-[#0a0a0a] border-red-500/30 text-white font-mono"
              data-testid="harddel-fit-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setConfirmDelete(null); setConfirmInput(''); }}
              className="border-[#2a2a4e] text-white"
              data-testid="harddel-fit-cancel"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={hardDelete}
              disabled={confirmInput.trim().toUpperCase() !== 'DELETE' || processingId === confirmDelete?.user_id}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="harddel-fit-confirm-btn"
            >
              {processingId === confirmDelete?.user_id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Trash2 className="h-4 w-4 me-2" />{language === 'ar' ? 'حذف نهائي' : 'Delete forever'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
